/**
 * Main state machine hook for the auth flow component.
 *
 * Orchestrates email lookup, password login, magic link auth, and signup flows.
 * Manages state transitions and coordinates sub-hooks.
 *
 * Aliases: auth flow hook, authentication state machine
 */

import { useCallback, useState } from "react";

import { getClerkErrorMessage } from "~/lib/clerk-errors";

import type {
  AuthFlowFormData,
  AuthFlowState,
  AuthSuccessMethod,
  SignupMethod,
} from "./auth-flow-types";
import { useEmailLookup } from "./use-email-lookup";
import { useMagicLinkAuth } from "./use-magic-link-auth";
import type {
  ClientTrustChannel,
  SecondFactorStrategy,
} from "./use-password-login";
import { usePasswordLogin } from "./use-password-login";

export interface UseAuthFlowOptions {
  readonly signupMethod?: SignupMethod;
  readonly defaultEmail?: string;
  readonly defaultFirstName?: string;
  readonly onSuccess: (method: AuthSuccessMethod) => void;
  readonly onError?: (error: string) => void;
  readonly redirectUrl?: string;
}

export interface UseAuthFlowResult {
  readonly currentState: AuthFlowState;
  readonly formData: AuthFlowFormData;
  readonly isProcessing: boolean;
  readonly error: string;
  readonly clientTrustChannel: ClientTrustChannel | null;
  readonly secondFactorStrategies: readonly SecondFactorStrategy[];
  readonly submitEmail: (email: string) => Promise<void>;
  readonly submitPassword: (password: string) => Promise<void>;
  readonly submitClientTrustCode: (code: string) => Promise<void>;
  readonly resendClientTrustCode: () => Promise<void>;
  readonly submitSecondFactor: (
    code: string,
    strategy: SecondFactorStrategy,
  ) => Promise<void>;
  readonly submitSignup: (
    firstName: string,
    lastName: string,
    password?: string,
  ) => Promise<void>;
  readonly switchToMagicLink: () => void;
  readonly clearError: () => void;
}

export const useAuthFlow = ({
  signupMethod = "password",
  defaultEmail = "",
  defaultFirstName = "",
  onSuccess,
  onError,
  redirectUrl,
}: UseAuthFlowOptions): UseAuthFlowResult => {
  const [currentState, setCurrentState] = useState<AuthFlowState>("email");
  const [formData, setFormData] = useState<AuthFlowFormData>({
    email: defaultEmail,
    firstName: defaultFirstName,
    lastName: "",
    password: "",
  });

  const emailLookup = useEmailLookup();
  const passwordLogin = usePasswordLogin();
  const magicLinkAuth = useMagicLinkAuth((method) => {
    setCurrentState("completed");
    onSuccess(method);
  }, redirectUrl);

  const error = emailLookup.error || passwordLogin.error || magicLinkAuth.error;

  const isProcessing =
    emailLookup.isLoading ||
    passwordLogin.isLoading ||
    magicLinkAuth.isLoading ||
    magicLinkAuth.isWaiting;

  const clearError = useCallback(() => {
    emailLookup.clearError();
    passwordLogin.clearError();
    magicLinkAuth.clearError();
  }, [emailLookup, passwordLogin, magicLinkAuth]);

  const submitEmail = useCallback(
    async (email: string): Promise<void> => {
      try {
        setFormData((prev) => ({ ...prev, email }));

        const result = await emailLookup.lookupEmail(email);

        if (!result.exists) {
          // New user → show signup form
          setCurrentState("signup-credentials");
        } else if (result.hasPassword) {
          // Existing user with password → show password login
          setCurrentState("login-password");
        } else {
          // Existing passwordless user → auto-send magic link
          setCurrentState("login-magic");
          await magicLinkAuth.sendMagicLink("login", email);
        }
      } catch {
        onError?.(error);
      }
    },
    [emailLookup, magicLinkAuth, onError, error],
  );

  const submitPassword = useCallback(
    async (password: string): Promise<void> => {
      try {
        const result = await passwordLogin.login(formData.email, password);
        if (result.status === "needs_client_trust") {
          setCurrentState("login-client-trust");
          return;
        }
        if (result.status === "needs_second_factor") {
          setCurrentState("login-second-factor");
          return;
        }
        setCurrentState("completed");
        onSuccess("password");
      } catch {
        onError?.(error);
      }
    },
    [formData.email, passwordLogin, onSuccess, onError, error],
  );

  const submitClientTrustCode = useCallback(
    async (code: string): Promise<void> => {
      try {
        await passwordLogin.verifyClientTrust(code);
        setCurrentState("completed");
        onSuccess("client_trust");
      } catch {
        onError?.(error);
      }
    },
    [passwordLogin, onSuccess, onError, error],
  );

  const resendClientTrustCode = useCallback(async (): Promise<void> => {
    try {
      await passwordLogin.resendClientTrustCode();
    } catch {
      onError?.(error);
    }
  }, [passwordLogin, onError, error]);

  const submitSecondFactor = useCallback(
    async (code: string, strategy: SecondFactorStrategy): Promise<void> => {
      try {
        await passwordLogin.verifySecondFactor(code, strategy);
        setCurrentState("completed");
        onSuccess("second_factor");
      } catch (err) {
        // Derive the message from the thrown error rather than the `error`
        // state, which is stale within this render's closure.
        onError?.(
          getClerkErrorMessage(err, "Invalid or expired verification code."),
        );
      }
    },
    [passwordLogin, onSuccess, onError],
  );

  const submitSignup = useCallback(
    async (
      firstName: string,
      lastName: string,
      password?: string,
    ): Promise<void> => {
      try {
        setFormData((prev) => ({
          ...prev,
          firstName,
          lastName,
          password: password ?? "",
        }));

        const outcome = await magicLinkAuth.sendMagicLink("signup", {
          email: formData.email,
          firstName,
          lastName,
          ...(signupMethod === "email_link" ? {} : { password }),
        });

        // Only show the "check your email" screen when Clerk actually requires
        // email verification. When the sign-up completes immediately, the magic
        // link hook has already activated the session and fired onComplete
        // (→ "completed" + onSuccess); forcing "verify-email" here is what
        // stranded freshly-signed-in users on a false "check your email" screen
        // with no email ever sent.
        if (outcome === "awaiting_verification") {
          setCurrentState("verify-email");
        }
      } catch (err) {
        // Derive the message from the thrown error rather than the `error`
        // state, which is stale within this render's closure (see
        // submitSecondFactor).
        onError?.(
          getClerkErrorMessage(
            err,
            "We couldn't finish creating your account. Please try again.",
          ),
        );
      }
    },
    [formData.email, magicLinkAuth, onError, signupMethod],
  );

  const switchToMagicLink = useCallback(() => {
    setCurrentState("login-magic");
    void magicLinkAuth.sendMagicLink("login", formData.email);
  }, [formData.email, magicLinkAuth]);

  return {
    currentState,
    formData,
    isProcessing,
    error,
    clientTrustChannel: passwordLogin.clientTrustChannel,
    secondFactorStrategies: passwordLogin.secondFactorStrategies,
    submitEmail,
    submitPassword,
    submitClientTrustCode,
    resendClientTrustCode,
    submitSecondFactor,
    submitSignup,
    switchToMagicLink,
    clearError,
  };
};
